/**
 * ReconnectionModal Component
 *
 * Generic reconnection modal for all sync providers with loading states,
 * error display, and retry actions.
 *
 * Features:
 * - Self-contained connection logic via useProviderConnection hook
 * - Discriminated union state rendering (idle | connecting | success | needsReauth | offline | rateLimited | error)
 * - Provider-specific error messages from syncErrorMessages utility
 * - Full accessibility support (WCAG 2.1 AA)
 * - Success auto-close (3 seconds)
 * - Rate-limit countdown
 * - Cross-tab coordination (handled by hook)
 * - **Toasts intentionally suppressed**: Modal provides all feedback internally to prevent duplicate/confusing notifications
 *
 * @example
 * ```tsx
 * const [isOpen, setIsOpen] = useState(false);
 *
 * <ReconnectionModal
 *   isOpen={isOpen}
 *   onOpenChange={setIsOpen}
 *   providerKey="google-drive"
 *   userId={user.id}
 *   onSuccess={() => console.log('Connected!')}
 * />
 * ```
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Type imports
import type { ProviderKey } from '@/types/sync';

// Hook imports
import { useSyncConnection } from '@/contexts';

// UI Component imports
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@enfomentis/ui';
import { Button } from '@enfomentis/ui';
import { Alert } from '@enfomentis/ui';
import { Spinner } from '@enfomentis/ui';

// Utility imports
import { getSyncErrorMessage, openOAuthPopup, syncInitReasonToErrorCode } from '@/utils';

/**
 * Props for ReconnectionModal component
 */
export interface ReconnectionModalProps {
  /** Whether the modal is currently open */
  isOpen: boolean;
  /** Callback when modal open state changes */
  onOpenChange: (open: boolean) => void;
  /** Provider key to reconnect to */
  providerKey: ProviderKey;
  /** Optional callback after successful reconnection */
  onSuccess?: (providerKey: ProviderKey) => void;
  /** Optional callback when user cancels */
  onCancel?: () => void;
}

/**
 * Helper function to get provider display name from ProviderKey.
 *
 * @param providerKey - The provider key to convert
 * @returns Human-readable provider name
 */
function getProviderDisplayName(providerKey: ProviderKey): string {
  switch (providerKey) {
    case 'google-drive':
      return 'Google Drive';
    case 'local':
      return 'Local Directory';
    default: {
      // Exhaustiveness check
      const _exhaustive: never = providerKey;
      console.warn('[ReconnectionModal] Unknown provider:', _exhaustive);
      return 'Unknown Provider';
    }
  }
}

/**
 * ReconnectionModal Component
 *
 * Generic modal for reconnecting to sync providers with comprehensive
 * state management and user feedback.
 */
export function ReconnectionModal(props: ReconnectionModalProps): React.ReactElement {
  const {
    isOpen,
    onOpenChange,
    providerKey,
    onSuccess,
    onCancel,
  } = props;

  // ===== HOOK INTEGRATION =====

  const { state, connect, abort } = useSyncConnection();

  // ===== STATE MANAGEMENT =====

  // Countdown for rate limiting
  const [countdown, setCountdown] = useState<number>(0);

  // Popup blocked state (for google-drive OAuth)
  const [popupBlocked, setPopupBlocked] = useState<boolean>(false);

  // Auto-close timer for success state
  const [autoCloseTimer, setAutoCloseTimer] = useState<ReturnType<typeof setTimeout> | null>(null);

  // Countdown interval ref
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Ref for primary action button (for automatic focus management)
  const primaryButtonRef = useRef<HTMLButtonElement>(null);

  // ===== COMPUTED VALUES =====

  // Provider display name
  const providerDisplayName = useMemo(
    () => getProviderDisplayName(providerKey),
    [providerKey]
  );

  // Compute dismissability based on state
  // Only non-dismissable during active connection (to allow abort)
  // All other states (including needsReauth) are dismissable
  const isDismissable = useMemo(() => {
    if (state.status === 'connecting') {
      return false; // Non-dismissable during connection (abort in-flight)
    }
    return true; // All other states are dismissable (including needsReauth)
  }, [state.status]);

  // Extract reason for error state (used in ariaLiveMessage dependency)
  const errorReason = state.status === 'error' ? state.reason : undefined;

  // Aria-live announcement message
  const ariaLiveMessage = useMemo(() => {
    switch (state.status) {
      case 'connecting':
        return `Reconnecting to ${providerDisplayName}`;
      case 'remoteConnecting':
        return `Another tab is reconnecting to ${providerDisplayName}`;
      case 'offline':
        return 'You are offline';
      case 'needsReauth':
        return 'Authentication required';
      case 'rateLimited':
        return 'Too many attempts. Please wait';
      case 'error':
        // Softer messages for user-initiated cancellation/denial
        if (errorReason === 'USER_CANCELLED') {
          return 'Connection cancelled';
        }
        if (errorReason === 'ACCESS_DENIED') {
          return 'Access denied';
        }
        if (errorReason === 'INSUFFICIENT_SCOPES') {
          return 'File access required';
        }
        if (errorReason === 'PROVIDER_OUTAGE') {
          return 'Service unavailable';
        }
        return 'Connection failed';
      case 'success':
        return 'Successfully connected';
      case 'idle':
        return '';
      default:
        return '';
    }
  }, [state.status, errorReason, providerDisplayName]);

  // ===== EFFECTS =====

  /**
   * Effect: Success auto-close (3 seconds)
   */
  useEffect(() => {
    if (state.status === 'success') {
      // Call onSuccess callback immediately
      onSuccess?.(providerKey);

      // Set timer to close modal after 3 seconds
      const timer = setTimeout(() => {
        onOpenChange(false);
      }, 3000);

      setAutoCloseTimer(timer);

      // Cleanup
      return () => {
        clearTimeout(timer);
        setAutoCloseTimer(null);
      };
    }
    return undefined;
  }, [state.status, onOpenChange, onSuccess, providerKey]);

  /**
   * Effect: Rate limit countdown
   */
  useEffect(() => {
    // Extract retryAfterMs to avoid complex expression in dependency array
    const retryAfterMs = state.status === 'rateLimited' ? state.retryAfterMs : 0;

    if (state.status === 'rateLimited') {
      // Guard: If retryAfterMs is invalid, set countdown to 0 and return
      if (retryAfterMs <= 0) {
        setCountdown(0);
        return undefined;
      }

      // Initialize countdown
      const initialCountdown = Math.ceil(retryAfterMs / 1000);
      setCountdown(initialCountdown);

      // Set interval to decrement countdown
      const interval = setInterval(() => {
        setCountdown((prev) => {
          const next = prev - 1;
          if (next <= 0) {
            // Clear interval when countdown reaches 0
            if (countdownIntervalRef.current) {
              clearInterval(countdownIntervalRef.current);
              countdownIntervalRef.current = null;
            }
            return 0;
          }
          return next;
        });
      }, 1000);

      countdownIntervalRef.current = interval;

      // Cleanup
      return () => {
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
        }
      };
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.status, state.status === 'rateLimited' ? state.retryAfterMs : 0]);

  /**
   * Effect: Cleanup on unmount or when modal closes
   *
   * Ensures timers, intervals, and stale state are cleaned up when modal closes,
   * not just on unmount.
   */
  useEffect(() => {
    // If modal is closing, clear timers and reset state immediately
    if (!isOpen) {
      if (autoCloseTimer) {
        clearTimeout(autoCloseTimer);
        setAutoCloseTimer(null);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      // Reset popupBlocked so stale state doesn't persist on reopen
      setPopupBlocked(false);
    }

    // Cleanup on unmount
    return () => {
      if (autoCloseTimer) {
        clearTimeout(autoCloseTimer);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, [isOpen, autoCloseTimer]);

  /**
   * Effect: Automatic focus management
   *
   * Moves focus to the primary action button when modal opens or state changes.
   * Uses requestAnimationFrame to ensure focus happens after the browser has
   * rendered the modal and HeroUI's focus-trap has initialized.
   *
   * This is required for WCAG 2.1 AA compliance: "focus moves to the modal when opened"
   */
  useEffect(() => {
    if (isOpen && primaryButtonRef.current) {
      // Schedule focus for the next animation frame
      // This ensures the modal and focus-trap are fully rendered first
      const focusTimerId = requestAnimationFrame(() => {
        primaryButtonRef.current?.focus();
      });

      // Cleanup: cancel the scheduled focus if modal closes before it fires
      return () => {
        cancelAnimationFrame(focusTimerId);
      };
    }
    return undefined;
  }, [isOpen, state.status]);

  // ===== EVENT HANDLERS =====

  /**
   * Handle retry action (reconnect)
   *
   * Re-entrancy guard: Only allow connection if not already connecting.
   * This prevents double-click issues before state flips to 'connecting'.
   *
   * For google-drive: Opens OAuth popup synchronously to preserve user gesture context.
   * If popup is blocked, shows error message and bails early.
   *
   * For local-directory: No popup needed, proceeds directly to connect.
   */
  const handleRetry = useCallback(() => {
    if (state.status === 'connecting') {
      return;
    }

    // Open popup synchronously (returns null for non-google-drive providers)
    const popup = openOAuthPopup(providerKey);

    // Guard for google-drive: if popup was blocked, show message and bail
    if (providerKey === 'google-drive' && !popup) {
      setPopupBlocked(true);
      return;
    }

    // Clear popup blocked state on successful popup open (for google-drive)
    if (providerKey === 'google-drive' && popup) {
      setPopupBlocked(false);
    }

    // Proceed with connection (local-directory: popup is null, still connect)
    connect(providerKey, { oauthPopup: popup });
  }, [connect, providerKey, state.status]);

  /**
   * Handle cancel action
   */
  const handleCancel = useCallback(() => {
    if (state.status === 'connecting') {
      // Abort in-flight connection
      abort();
    } else {
      // Close modal and call onCancel callback
      onCancel?.();
      onOpenChange(false);
    }
  }, [state.status, abort, onCancel, onOpenChange]);

  /**
   * Wrapped onOpenChange handler
   *
   * Ensures onCancel callback is called when modal is dismissed via:
   * - Escape key
   * - Backdrop click
   * - Close button (X)
   *
   * This is required for proper dismissal tracking in PrivateAppProvider.
   * Without this, modalDismissedRef stays false and modal reopens immediately.
   *
   * IMPORTANT: Only treat as cancellation for error/needsReauth states,
   * NOT for success auto-close (which should clear modalDismissedRef, not set it).
   */
  const handleOpenChange = useCallback((open: boolean) => {
    // Determine if this close should be treated as user cancellation
    // Only mark as cancelled for dismissable error/needsReauth states
    // Exclude success (auto-close should not mark as dismissed)
    const canMarkCancelled = isDismissable && state.status !== 'success';

    // If closing the modal AND it's a cancellable state, call onCancel
    if (!open && canMarkCancelled) {
      // Call onCancel to trigger dismissal tracking
      onCancel?.();
    }

    // Always call parent onOpenChange
    onOpenChange(open);
  }, [isDismissable, state.status, onCancel, onOpenChange]);

  // ===== RENDER =====

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      size="md"
      placement="center"
      backdrop="blur"
      isDismissable={isDismissable}
      isKeyboardDismissDisabled={!isDismissable}
      hideCloseButton={!isDismissable}
      role="dialog"
      aria-busy={state.status === 'connecting' ? 'true' : undefined}
      aria-labelledby="reconnection-modal-title"
      aria-describedby="reconnection-modal-description"
    >
      <ModalContent>
        {/* Screenreader-only live region for state announcements */}
        <p className="sr-only" aria-live="polite" aria-atomic="true">
          {ariaLiveMessage}
        </p>

        {/* Render content based on state */}
        {state.status === 'connecting' && (
          <>
            <ModalHeader>
              <h3 id="reconnection-modal-title" className="text-lg font-semibold">
                Reconnecting
              </h3>
            </ModalHeader>
            <ModalBody>
              <div className="flex items-center justify-center gap-3 py-8">
                <Spinner size="lg" color="primary" />
                <p>Reconnecting to {providerDisplayName}...</p>
              </div>
              <p id="reconnection-modal-description" className="sr-only">
                Attempting to reconnect. Please wait.
              </p>
            </ModalBody>
            <ModalFooter>
              <Button
                color="default"
                variant="light"
                onPress={handleCancel}
              >
                Cancel
              </Button>
            </ModalFooter>
          </>
        )}

        {state.status === 'remoteConnecting' && (
          <>
            <ModalHeader>
              <h3 id="reconnection-modal-title" className="text-lg font-semibold">
                Reconnecting
              </h3>
            </ModalHeader>
            <ModalBody>
              <div className="flex items-center justify-center gap-3 py-8">
                <Spinner size="lg" color="primary" />
                <p>Another tab is reconnecting to {providerDisplayName}...</p>
              </div>
              <p id="reconnection-modal-description" className="sr-only">
                Another browser tab is attempting to reconnect. Please wait.
              </p>
            </ModalBody>
            <ModalFooter>
              <Button
                color="primary"
                isDisabled={true}
              >
                Reconnect
              </Button>
              <Button
                ref={primaryButtonRef}
                color="default"
                variant="light"
                onPress={handleCancel}
              >
                Close
              </Button>
            </ModalFooter>
          </>
        )}

        {state.status === 'offline' && (
          <>
            <ModalHeader>
              <h3 id="reconnection-modal-title" className="text-lg font-semibold">
                Connection Required
              </h3>
            </ModalHeader>
            <ModalBody>
              <Alert color="warning" variant="flat" role="status">
                <p>
                  Reconnect is unavailable while offline. Check your connection and try again.
                </p>
              </Alert>
              <p id="reconnection-modal-description" className="sr-only">
                You are offline. Cannot connect to sync provider.
              </p>
            </ModalBody>
            <ModalFooter>
              <Button
                ref={primaryButtonRef}
                color="primary"
                isDisabled={true}
                onPress={handleRetry}
              >
                Retry
              </Button>
              <Button
                color="default"
                variant="light"
                onPress={handleCancel}
              >
                Close
              </Button>
            </ModalFooter>
          </>
        )}

        {state.status === 'needsReauth' && (() => {
          // Get error message using adapter + utility
          const errorCode = syncInitReasonToErrorCode(state.reason);
          const syncError = {
            code: errorCode,
            message: state.error,
            provider: providerKey === 'google-drive' ? ('google-drive' as const) : ('local' as const),
          };
          const errorMessage = getSyncErrorMessage(syncError, 'modal', providerDisplayName);

          return (
            <>
              <ModalHeader>
                <h3 id="reconnection-modal-title" className="text-lg font-semibold">
                  {errorMessage.title}
                </h3>
              </ModalHeader>
              <ModalBody>
                <Alert color="danger" variant="flat" role="alert">
                  <p>{errorMessage.description}</p>
                </Alert>
                {popupBlocked && (
                  <Alert color="warning" variant="flat" role="alert" className="mt-3">
                    <p>Please allow popups for this site and try again.</p>
                  </Alert>
                )}
                <p id="reconnection-modal-description" className="sr-only">
                  {errorMessage.description}
                </p>
              </ModalBody>
              <ModalFooter>
                <Button
                  ref={primaryButtonRef}
                  color="primary"
                  onPress={handleRetry}
                >
                  {errorMessage.actionText || 'Re-authenticate'}
                </Button>
                <Button
                  color="default"
                  variant="light"
                  onPress={handleCancel}
                >
                  Skip for now
                </Button>
              </ModalFooter>
            </>
          );
        })()}

        {state.status === 'rateLimited' && (
          <>
            <ModalHeader>
              <h3 id="reconnection-modal-title" className="text-lg font-semibold">
                Too Many Attempts
              </h3>
            </ModalHeader>
            <ModalBody>
              <Alert color="warning" variant="flat" role="status">
                <p>
                  Too many connection attempts. Please try again in {countdown} second
                  {countdown !== 1 ? 's' : ''}.
                </p>
              </Alert>
              {popupBlocked && (
                <Alert color="warning" variant="flat" role="alert" className="mt-3">
                  <p>Please allow popups for this site and try again.</p>
                </Alert>
              )}
              <p id="reconnection-modal-description" className="sr-only">
                Rate limited. Retry after countdown.
              </p>
            </ModalBody>
            <ModalFooter>
              <Button
                ref={primaryButtonRef}
                color="primary"
                isDisabled={countdown > 0}
                onPress={handleRetry}
              >
                {countdown > 0 ? `Retry (${countdown}s)` : 'Retry'}
              </Button>
              <Button
                color="default"
                variant="light"
                onPress={handleCancel}
              >
                Close
              </Button>
            </ModalFooter>
          </>
        )}

        {state.status === 'error' && (() => {
          // Check if user cancelled (closed popup before completing OAuth)
          const isUserCancelled = state.reason === 'USER_CANCELLED';

          // USER_CANCELLED: Show softer messaging (warning, not error)
          if (isUserCancelled) {
            const cancelledMessage = 'You closed the popup before completing authentication. Click Reconnect to try again.';
            return (
              <>
                <ModalHeader>
                  <h3 id="reconnection-modal-title" className="text-lg font-semibold">
                    Connection Cancelled
                  </h3>
                </ModalHeader>
                <ModalBody>
                  <Alert color="warning" variant="flat" role="status">
                    <p>{cancelledMessage}</p>
                  </Alert>
                  <p id="reconnection-modal-description" className="sr-only">
                    {cancelledMessage}
                  </p>
                </ModalBody>
                <ModalFooter>
                  <Button
                    ref={primaryButtonRef}
                    color="primary"
                    onPress={handleRetry}
                  >
                    Reconnect
                  </Button>
                  <Button
                    color="default"
                    variant="light"
                    onPress={handleCancel}
                  >
                    Close
                  </Button>
                </ModalFooter>
              </>
            );
          }

          // ACCESS_DENIED: User clicked "Deny" on OAuth consent screen
          if (state.reason === 'ACCESS_DENIED') {
            const accessDeniedMessage = 'You denied access to Google Drive. Click Reconnect to try again.';
            return (
              <>
                <ModalHeader>
                  <h3 id="reconnection-modal-title" className="text-lg font-semibold">
                    Access Denied
                  </h3>
                </ModalHeader>
                <ModalBody>
                  <Alert color="warning" variant="flat" role="status">
                    <p>{accessDeniedMessage}</p>
                  </Alert>
                  <p id="reconnection-modal-description" className="sr-only">
                    {accessDeniedMessage}
                  </p>
                </ModalBody>
                <ModalFooter>
                  <Button
                    ref={primaryButtonRef}
                    color="primary"
                    onPress={handleRetry}
                  >
                    Reconnect
                  </Button>
                  <Button
                    color="default"
                    variant="light"
                    onPress={handleCancel}
                  >
                    Close
                  </Button>
                </ModalFooter>
              </>
            );
          }

          // INSUFFICIENT_SCOPES: User denied file access scope (granular consent)
          if (state.reason === 'INSUFFICIENT_SCOPES') {
            const insufficientScopesMessage = 'File access permission is required. Please allow file access when prompted to sync your trading journal.';
            return (
              <>
                <ModalHeader>
                  <h3 id="reconnection-modal-title" className="text-lg font-semibold">
                    File Access Required
                  </h3>
                </ModalHeader>
                <ModalBody>
                  <Alert color="warning" variant="flat" role="status">
                    <p>{insufficientScopesMessage}</p>
                  </Alert>
                  <p id="reconnection-modal-description" className="sr-only">
                    {insufficientScopesMessage}
                  </p>
                </ModalBody>
                <ModalFooter>
                  <Button
                    ref={primaryButtonRef}
                    color="primary"
                    onPress={handleRetry}
                  >
                    Reconnect
                  </Button>
                  <Button
                    color="default"
                    variant="light"
                    onPress={handleCancel}
                  >
                    Close
                  </Button>
                </ModalFooter>
              </>
            );
          }

          // PROVIDER_OUTAGE: Google server error (outage, temporary issue)
          if (state.reason === 'PROVIDER_OUTAGE') {
            const outageMessage = 'Google is experiencing issues. Please try again in a few minutes.';
            return (
              <>
                <ModalHeader>
                  <h3 id="reconnection-modal-title" className="text-lg font-semibold">
                    Service Unavailable
                  </h3>
                </ModalHeader>
                <ModalBody>
                  <Alert color="warning" variant="flat" role="status">
                    <p>{outageMessage}</p>
                  </Alert>
                  {popupBlocked && (
                    <Alert color="warning" variant="flat" role="alert" className="mt-3">
                      <p>Please allow popups for this site and try again.</p>
                    </Alert>
                  )}
                  <p id="reconnection-modal-description" className="sr-only">
                    {outageMessage}
                  </p>
                </ModalBody>
                <ModalFooter>
                  <Button
                    ref={primaryButtonRef}
                    color="primary"
                    onPress={handleRetry}
                  >
                    Try Again
                  </Button>
                  <Button
                    color="default"
                    variant="light"
                    onPress={handleCancel}
                  >
                    Close
                  </Button>
                </ModalFooter>
              </>
            );
          }

          // Other errors: Get error message using adapter + utility
          const errorCode = syncInitReasonToErrorCode(state.reason);
          const syncError = {
            code: errorCode,
            message: state.error,
            provider: providerKey === 'google-drive' ? ('google-drive' as const) : ('local' as const),
          };
          const errorMessage = getSyncErrorMessage(syncError, 'modal', providerDisplayName);

          return (
            <>
              <ModalHeader>
                <h3 id="reconnection-modal-title" className="text-lg font-semibold">
                  {errorMessage.title}
                </h3>
              </ModalHeader>
              <ModalBody>
                <Alert color="danger" variant="flat" role="alert">
                  <p>{errorMessage.description}</p>
                </Alert>
                {popupBlocked && (
                  <Alert color="warning" variant="flat" role="alert" className="mt-3">
                    <p>Please allow popups for this site and try again.</p>
                  </Alert>
                )}
                <p id="reconnection-modal-description" className="sr-only">
                  {errorMessage.description}
                </p>
              </ModalBody>
              <ModalFooter>
                <Button
                  ref={primaryButtonRef}
                  color="primary"
                  onPress={handleRetry}
                >
                  {errorMessage.actionText || 'Retry'}
                </Button>
                <Button
                  color="default"
                  variant="light"
                  onPress={handleCancel}
                >
                  Close
                </Button>
              </ModalFooter>
            </>
          );
        })()}

        {state.status === 'success' && (
          <>
            <ModalHeader>
              <h3 id="reconnection-modal-title" className="text-lg font-semibold">
                Connected
              </h3>
            </ModalHeader>
            <ModalBody>
              <div className="text-center py-8">
                <div className="text-success-600 text-6xl mb-4">✓</div>
                <h4 className="text-lg font-medium text-text-primary mb-2">
                  Successfully Connected
                </h4>
                <p className="text-text-secondary">
                  Connected to {providerDisplayName}. You're good to go—sync will resume automatically.
                </p>
                <p className="text-text-secondary text-sm mt-2">
                  This modal will close in 3 seconds.
                </p>
              </div>
              <p id="reconnection-modal-description" className="sr-only">
                Successfully connected to sync provider.
              </p>
            </ModalBody>
            {/* No footer for success state (auto-close) */}
          </>
        )}

        {state.status === 'idle' && (
          <>
            <ModalHeader>
              <h3 id="reconnection-modal-title" className="text-lg font-semibold">
                Reconnection
              </h3>
            </ModalHeader>
            <ModalBody>
              <p id="reconnection-modal-description">
                Modal is in idle state. This should not be visible.
              </p>
            </ModalBody>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
